import { AxiosError } from "axios";
import { TFunction } from "i18next";
import { FieldValues, UseFormSetError } from "react-hook-form";

export interface ProblemDetails {
  title: string;
  detail: string;
  status?: number;
  field?: string; // Newtonsoft.Json serializes Extensions to root level
  extensions?: {
    field?: string;
  };
}

export interface HandleProblemDetailsErrorOptions<
  TFormValues extends FieldValues
> {
  error: AxiosError<ProblemDetails>;
  t: TFunction;
  setError?: UseFormSetError<TFormValues>;
  formValues?: TFormValues;
  showToast?: (message: string, type: "error" | "success" | "info") => void;
  fallbackMessage?: string;
}

/**
 * Handles ProblemDetails error responses (RFC 7807) from the backend.
 * Sets form field errors when field is specified, otherwise shows toast.
 *
 * @param options - Configuration options for error handling
 * @returns true if error was handled, false otherwise
 */
export function handleProblemDetailsError<TFormValues extends FieldValues>(
  options: HandleProblemDetailsErrorOptions<TFormValues>
): boolean {
  const { error, t, setError, showToast, fallbackMessage } = options;
  const problemDetails = error.response?.data;

  // Validate ProblemDetails format
  if (
    !problemDetails ||
    typeof problemDetails !== "object" ||
    !("title" in problemDetails)
  ) {
    if (showToast) {
      showToast(fallbackMessage || t("errors.somethingWentWrong"), "error");
    }
    return false;
  }

  // Get field name (Newtonsoft.Json serializes Extensions to root level)
  const fieldName = problemDetails.field || problemDetails.extensions?.field;

  // Build translated message
  const translatedMessage = fieldName
    ? t(`errorCodes.${problemDetails.title}`, {
      fieldName: t(`common.${fieldName}`),
      defaultValue: problemDetails.detail,
    }).replaceAll("{{fieldName}}", t(`common.${fieldName}`)) ||
      problemDetails.detail
    : t(`errorCodes.${problemDetails.title}`, {
      defaultValue: problemDetails.detail,
    });

  // Set error on field if field specified and exists in formValues
  if (fieldName && setError) {
    // Check if field exists in formValues if formValues is provided
    if (options.formValues && !(fieldName in options.formValues)) {
      // Field doesn't exist in formValues - show toast instead
      if (showToast) {
        showToast(translatedMessage, "error");
      }
      return true;
    }
    
    // Field exists in formValues (or formValues not provided) - set error on field
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setError(fieldName as any, {
      type: "manual",
      message: translatedMessage,
    });
    return true;
  }

  // Field not specified or doesn't exist - show toast
  if (showToast) {
    showToast(translatedMessage, "error");
  }

  return true;
}
